import { QueryClient } from '@tanstack/react-query';
import { LoaderFunction } from 'react-router';

import { DashboardContext } from 'src/routes/dashboard/container';
import { assertDefined } from 'src/utils';

import { queries } from './queries';

// oxlint-disable-next-line typescript/no-unnecessary-type-parameters
export function loadContext<T>(): T {
  const element = document.getElementById('context-vars');
  return JSON.parse(element?.textContent ?? '{}') as T;
}

export type LoaderData<T> = T extends (...args: infer _A) => infer R
  ? R extends (...args: infer _A2) => infer R2
    ? Awaited<R2>
    : never
  : never;

export const loaders = {
  usecaseLoader: (queryClient: QueryClient) => {
    return async ({ params: { usecaseId } }) => {
      assertDefined(usecaseId);
      const { store } = loadContext<DashboardContext>();
      assertDefined(store);
      const query = queries.usecase(store.ownerOrganization, parseInt(usecaseId, 10));
      return queryClient.ensureQueryData(query);
    };
  },
  usecasesForStoreLoader: (queryClient: QueryClient) => {
    return async ({ params: { storeKey } }) => {
      assertDefined(storeKey);
      const { store } = loadContext<DashboardContext>();
      assertDefined(store);
      const query = queries.usecasesForStore(store.ownerOrganization, storeKey);
      return queryClient.ensureQueryData(query);
    };
  },
  spOptimizerStoresLoader: (queryClient: QueryClient) => {
    return async () => {
      const { store } = loadContext<DashboardContext>();
      assertDefined(store);
      const query = queries.spOptimizerStores(store.ownerOrganization);
      return queryClient.ensureQueryData(query);
    };
  },
  spOptimizerStoreExcludePhrases: (queryClient: QueryClient) => {
    return async ({ params: { spOptimizerStoreId } }) => {
      assertDefined(spOptimizerStoreId);
      const { store } = loadContext<DashboardContext>();
      assertDefined(store);
      const query = queries.spOptimizerStoreExcludePhrases(store.ownerOrganization, parseInt(spOptimizerStoreId, 10));
      return queryClient.ensureQueryData(query);
    };
  },
  spOptimizerStoreIncludePhrases: (queryClient: QueryClient) => {
    return async ({ params: { spOptimizerStoreId } }) => {
      assertDefined(spOptimizerStoreId);
      const { store } = loadContext<DashboardContext>();
      assertDefined(store);
      const query = queries.spOptimizerStoreIncludePhrases(store.ownerOrganization, parseInt(spOptimizerStoreId, 10));
      return queryClient.ensureQueryData(query);
    };
  },
  spOptimizerFlowMessageExperimentsForStoreLoader: (queryClient: QueryClient) => {
    return async ({ params: { storeKey } }) => {
      assertDefined(storeKey);
      const { store } = loadContext<DashboardContext>();
      assertDefined(store);
      const query = queries.spOptimizerFlowMessageExperimentsForStore(store.ownerOrganization, storeKey);
      return queryClient.ensureQueryData(query);
    };
  },
  spOptimizerFlowMessageExperimentsForSPOptimizerStoreLoader: (queryClient: QueryClient) => {
    return async ({ params: { spOptimizerStoreId } }) => {
      assertDefined(spOptimizerStoreId);
      const { store } = loadContext<DashboardContext>();
      assertDefined(store);
      const query = queries.spOptimizerFlowMessageExperimentsForSPOptimizerStore(
        store.ownerOrganization,
        parseInt(spOptimizerStoreId, 10),
      );
      return queryClient.ensureQueryData(query);
    };
  },
} as const satisfies Record<string, (queryClient: QueryClient) => LoaderFunction>;
